/**
 * ResourceBadgeSubscriber — pushes "Courses in Draft" via SignalR.
 *
 * Phase 4 of the badge migration: replaces the 30s poll with one REST
 * baseline + WS deltas from CoursePublishedBadgeConsumer.
 *
 * kbInDraft is hard-coded to 0 until the KB SDK hooks are wired (same
 * caveat as the original polling version).
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useCourseService,
  useDomainDashboard,
  type BadgeDeltaMessage,
  type BadgeSnapshotMessage,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useResourceBadgesStore } from './resourceBadgesStore';

const RESOURCE_ROLES = ['Resources Access', 'Admin'];
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
  if (DEBUG) console.log('[ResourceBadgeSubscriber]', ...args); // eslint-disable-line no-console
};

interface ResourceDeltas {
  coursesInDraft?: number;
  kbInDraft?: number;
}

export function ResourceBadgeSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const enabled = hasAnyRole(RESOURCE_ROLES);

  const courseService = useCourseService();
  const setFromMetrics = useResourceBadgesStore((s) => s.setFromMetrics);
  const applyDelta = useResourceBadgesStore((s) => s.applyDelta);
  const reset = useResourceBadgesStore((s) => s.reset);

  const refetchBaseline = useCallback(async () => {
    try {
      const courses = (await courseService.getAll()) as any[];
      const coursesInDraft = (courses ?? []).filter((c: any) => !c?.publishedDateTime).length;
      log('baseline', { coursesInDraft, total: courses?.length ?? 0 });
      setFromMetrics({ coursesInDraft, kbInDraft: 0 });
    } catch (err) {
      log('baseline fetch failed', err);
    }
  }, [courseService, setFromMetrics]);

  useEffect(() => {
    if (!enabled) return;
    refetchBaseline();
  }, [enabled, refetchBaseline]);

  const { isConnected, requestBadgeSnapshot } = useDomainDashboard('resources', {
    autoConnect: enabled,
    autoSubscribe: false,
    onBadgeSnapshot: (msg: BadgeSnapshotMessage<ResourceDeltas>) => {
      if (msg.domain !== 'Resources') return;
      log('snapshot', msg.snapshot);
      setFromMetrics(msg.snapshot ?? {});
    },
    onBadgeDelta: (msg: BadgeDeltaMessage<ResourceDeltas>) => {
      if (msg.domain !== 'resources') return;
      log('delta', msg.reason, msg.deltas);
      applyDelta(msg.deltas ?? {});
    },
  });

  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
      return;
    }
    if (isConnected && wasConnectedRef.current) {
      log('reconnected → refetch baseline');
      refetchBaseline();
      void requestBadgeSnapshot();
    }
  }, [enabled, isConnected, refetchBaseline, requestBadgeSnapshot]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    log('mounted', { enabled });
  }, [enabled]);

  return null;
}
